import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import React from 'react';
import useRect from 'hooks/common/useRect';
import { Chart } from 'chart.js';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useReportInsulinStatsGraph from 'hooks/communication/useReportInsulinStatsGraph';
import * as Luxon from 'luxon'
import { luxonClone } from 'utils/time';
import useSetChartData from 'hooks/app/useSetChartData';

// const dataLabels = Array.from({ length: 13 }).map((_, index) => `7/${8 + index}`);
const config = (hash, onComplete, duration) => ({
  type: 'bar',
  data: {
    labels: hash.dataLabels,
    datasets: [
      {
        barThickness: hash.dataLabels.length > 30 ? 10 : 20,
        label: T('기초 인슐린 투여량'),
        data: hash.base,
        backgroundColor: '#AFB6C0',
      }, {
        barThickness: hash.dataLabels.length > 30 ? 10 : 20,
        label: T('볼루스 인슐린 투여량'),
        data: hash.bolus,
        backgroundColor: '#5F9FFF',
        borderRadius: 10,
      },
    ]
  },
  options: {
    devicePixelRatio: 4,
    animation: {
      duration,
      onComplete,
    },
    layout: {
      padding: {
        top: 30,
        left: 30,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        border: {
          dash: [4, 4],
        },
        stacked: true,
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
        },
        ticks: {
          display: true,
          font: {
            weight: 400,
            size: 11,
            color: '#000000',
          },
          color: '#000000',
        },
      },
      y: {
        border: { display: false },
        stacked: true,
        grid: {
          display: true,
          drawTicks: false,
          drawOnChartArea: true,
        },
        ticks: {
          min: 0,
          max: 60,
          display: true,
          stepSize: 20,
          color: '#aaaaaa',
          font: {
            weight: 400,
            size: 11,
            color: '#aaaaaa',
          },
        },
      }
    }
  },
  plugins: [{
    afterDatasetDraw: (chart, args, options) => {
      const ctx = chart.ctx;
      const yAxis = chart.scales.y;
      const xAxis = chart.scales.x;

      const meta = args.meta;
      const dataset = meta.controller.getDataset();
      if (!dataset) return;

      if (!yAxis) return;
      if (!xAxis) return;

      const left = xAxis.left;
      const right = xAxis.right;

      ctx.save();

      ctx.font = '12px Pretendard';
      ctx.fillStyle = '#bbbbbb';
      const y = yAxis.getPixelForValue(2000);

      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText(T('칼로리(kcal)'), left - 3, y - 15);

      ctx.restore();
    }
  }]
});

export default function InsulinStatisticsChart(props) {
  const {
    duration,
    ...rest
  } = props;

  const {
    requestReportInsulinStatsGraph,
  } = useReportInsulinStatsGraph();

  const [uninitialized, setUninitialized] = React.useState(true);

  const setChartData = useSetChartData('InsulinStatisticsDayChart');
  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartData && setChartData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [duration, setChartData, uninitialized]);

  const [list, setList] = React.useState([]);
  const period_start = Recoil.useRecoilValue(AppStore.assets.period_start);
  const period_end = Recoil.useRecoilValue(AppStore.assets.period_end);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  React.useEffect(() => {
    setUninitialized(() => true);
    requestReportInsulinStatsGraph({
      patient_no,
      search_start_time: period_start,
      search_end_time: period_end,
    }, (success, data) => {
      if (success) {
        setList(() => data.insulin_stat_time_graph);
        setUninitialized(() => false);
      }
    });
  }, [patient_no, period_end, period_start, requestReportInsulinStatsGraph]);

  const hash = React.useMemo(() => {
    const data = {
      dataLabels: [],
      base: [],
      bolus: [],
    };
    list.forEach(item => {
      const {
        time,
        bolus_amt,
        giju_amt,
      } = item;
      const luxon = Luxon.DateTime.fromFormat(time, 'yyyyMMdd');
      const date = luxon.toFormat("M/d");
      data.dataLabels.push(date);
      data.base.push(giju_amt);
      data.bolus.push(bolus_amt);
    });
    return data;
  }, [list]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const canvasRef = React.useRef();
  const chartRef = React.useRef();
  React.useEffect(() => {
    const chart = new Chart(canvasRef.current, config(hash, onComplete, duration));
    chartRef.current = chart;
    return () => {
      chart.destroy();
      delete chart.config;
    };
  }, [duration, hash, onComplete]);
  React.useLayoutEffect(() => {
    if (!chartRef.current) return;
    const frameId = requestAnimationFrame(() => {
      chartRef.current.update();
    });
    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [rect]);
  return (
    <div
      {...ex(['Chart', 'InsulinStatisticsChart'], rest)}
      {...{ ref }}
    >
      <canvas
        {...{
          ref: canvasRef,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>
  );
}
